/**
 * EMP-MASTER-001 — form + view renderers for the Employee Master sections.
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { ShieldAlert } from 'lucide-react';
import { MASTER_SECTIONS } from './employeeMasterFields';

export const EmployeeMasterFormSections = ({ formData, setFormData }) => {
  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6">
      {MASTER_SECTIONS.map(section => (
        <div key={section.title}>
          <h4 className="text-sm font-semibold text-slate-800 border-b border-slate-100 pb-1.5 mb-3 flex items-center gap-2">
            {section.title}
            {section.sensitive && <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />}
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {section.fields.map(f => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">{f.label}</label>
                {f.type === 'select' ? (
                  <Select
                    value={formData[f.key] || undefined}
                    onValueChange={(v) => setField(f.key, v)}
                  >
                    <SelectTrigger data-testid={`emp-master-${f.key}`}>
                      <SelectValue placeholder={`Select ${f.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {f.options.map(o => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === 'textarea' ? (
                  <Textarea
                    value={formData[f.key] || ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    rows={2}
                    data-testid={`emp-master-${f.key}`}
                  />
                ) : (
                  <Input
                    type={f.type === 'date' ? 'date' : 'text'}
                    value={formData[f.key] || ''}
                    onChange={(e) => setField(f.key, e.target.value)}
                    data-testid={`emp-master-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const EmployeeMasterViewSections = ({ employee }) => (
  <div className="space-y-5">
    {MASTER_SECTIONS.map(section => {
      const filled = section.fields.filter(f => employee?.[f.key]);
      if (!filled.length) return null;
      return (
        <div key={section.title}>
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-1.5 mb-3 flex items-center gap-2">
            {section.title}
            {section.sensitive && <ShieldAlert className="w-3.5 h-3.5 text-amber-500" />}
          </h4>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            {filled.map(f => (
              <div key={f.key} className={f.full ? 'col-span-2' : ''}>
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-0.5">{f.label}</p>
                <p className="text-sm font-medium text-slate-700" data-testid={`emp-view-${f.key}`}>
                  {employee[f.key]}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </div>
);
